import { apiRequest } from '@/domains/api';
import type {
  IFigureAddress,
  IFigureCell,
  IFigureHistory,
  IFigureTable
} from './figure-contracts';

/**
 * survey-api's figures (SR-222 block 5, `FigureEndpoints.cs`): a participant's table for one
 * survey, one cell's write, clear and history — the current values and rows of survey-api's
 * metrics journal (SR-451). The sole reader of `/api/v1/admin/participants/{id}/figures*`. The
 * freeze of a cycle's figures went with SR-451: a submitted answer is a metric already.
 *
 * **A 2xx that carries no table is a failed read**, refused here (the roster's `rosterOf` lesson):
 * a table drawn from a body without `metrics` and `rows` would throw while the hub draws, and this
 * app has no error boundary — **and so is one without `keptUnder`**, the survey a cell's write is
 * addressed by: falling back to the survey asked for would write a `CANADA_FR` cell under a
 * survey survey-api keeps no figures under. The same holds of a write's 200 without a cell, which the hub holds
 * over the table until its re-read lands, of a history without its `items` or with a `cell` that is
 * neither an object nor null, and of a freeze's 200 without its counts — whose "Nothing was
 * written" would otherwise be said of an answer that never said so. What is inside the lists is
 * not judged.
 *
 * **Every segment of an address is encoded as one segment.** Its parts come from survey-api's own
 * documents — the hub's participant ids, the table's metrics and scopes, its as-of days or a date
 * input's `yyyy-MM-dd` — never from the page's address, so the dot-segment guard of
 * `internal-fields-api.ts` (a code a document may spell any way) is not repeated here.
 *
 * **No `If-Match`, no `ETag`.** A figure is last-write-wins on survey-api's side (the roadmap's
 * B2, a ticket of its own).
 */

const BASE = '/api/v1/admin';

/** A cell's history is a popover's, ten a page — survey-api's default (50) is a list's. */
const HISTORY_PAGE_SIZE = 10;

function isObject(answer: unknown): answer is Record<string, unknown> {
  return typeof answer === 'object' && answer !== null;
}

function tableOf(answer: unknown): IFigureTable {
  if (
    !isObject(answer) ||
    typeof answer.keptUnder !== 'string' ||
    !Array.isArray(answer.metrics) ||
    !Array.isArray(answer.rows)
  ) {
    throw new Error('The answer held no figures table');
  }
  return answer as unknown as IFigureTable;
}

function cellOf(answer: unknown): IFigureCell {
  if (!isObject(answer) || typeof answer.origin !== 'string') {
    throw new Error('The answer to the write held no cell');
  }
  return answer as unknown as IFigureCell;
}

function historyOf(answer: unknown): IFigureHistory {
  if (
    !isObject(answer) ||
    !Array.isArray(answer.items) ||
    !(answer.cell === null || isObject(answer.cell))
  ) {
    throw new Error('The answer held no figure history');
  }
  return answer as unknown as IFigureHistory;
}

function tablePath(participantId: string): string {
  return `${BASE}/participants/${encodeURIComponent(participantId)}/figures`;
}

function cellPath(participantId: string, address: IFigureAddress): string {
  const { surveyCode, metric, scope, asOf } = address;
  return [tablePath(participantId), ...[surveyCode, metric, scope, asOf].map(encodeURIComponent)]
    .join('/');
}

export const figuresApi = {
  /**
   * One survey's figures about a participant (`internal_fields.read`, staff and admin): the
   * catalogue's columns and one row per as-of date, newest first — **the figures `keptUnder`
   * names**, `CANADA`'s for `CANADA_FR` (served by survey-api `30d88f37`). A known survey with
   * no catalogue answers empty `metrics`. Throws for a bodiless **404** — an unknown participant,
   * and nothing else (survey-api's D3) — and for a 400 `invalid-query` at `survey`, a code that is
   * none of survey-api's surveys.
   */
  table(participantId: string, surveyCode: string, signal?: AbortSignal): Promise<IFigureTable> {
    return apiRequest<unknown>(
      `${tablePath(participantId)}?survey=${encodeURIComponent(surveyCode)}`,
      { signal }
    ).then(tableOf);
  },

  /**
   * Sets one cell (`internal_fields.write`) — 200 with the cell as the table draws it, re-read, so
   * it carries the actor's email and its `change`. Writes `origin: staff`, at a date nothing else
   * filled too; the same value on a staff cell writes nothing. `reason` is left out when empty.
   * Refusals: 400 `invalid-body` at `value` (negative, 10^16 or more, more than two decimals —
   * refused, never rounded), the binder's 400 at `$.value`, a bodiless 404 (no participant,
   * survey, metric or scope). `address.surveyCode` is the table's `keptUnder`.
   */
  set(
    participantId: string,
    address: IFigureAddress,
    value: number,
    reason?: string
  ): Promise<IFigureCell> {
    return apiRequest<unknown>(cellPath(participantId, address), {
      method: 'PUT',
      body: reason ? { value, reason } : { value }
    }).then(cellOf);
  },

  /** Clears one cell — **204 whether or not a value was there**; the row stays with `value: null`.
   *  The reason travels as `?reason=` (a `DELETE` body is not reliably carried), only when given.
   *  A bodiless 404 as `set`'s. */
  clear(participantId: string, address: IFigureAddress, reason?: string): Promise<void> {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return apiRequest<null>(`${cellPath(participantId, address)}${query}`, {
      method: 'DELETE'
    }).then(() => undefined);
  },

  /** "Confirm unchanged" (SR-315, `internal_fields.write`): the participant's figures of the
   *  survey checked and unchanged as of `asOf` — one `confirmed` journal row per series, which
   *  sets "Data as of" and takes the participant off the round's Metrics updates lists. 204; a
   *  bodiless 404 as `set`'s; 400 at `asOf`. `surveyCode` is the figures' `keptUnder`. */
  confirm(participantId: string, surveyCode: string, asOf: string, reason?: string): Promise<void> {
    return apiRequest<null>(`${tablePath(participantId)}/${encodeURIComponent(surveyCode)}/confirmations`, {
      method: 'POST',
      body: reason ? { asOf, reason } : { asOf }
    }).then(() => undefined);
  },

  /** One cell's history: the cell as it is now and a page of its events, newest first, ten a
   *  page. An address no row holds answers **200 with `cell: null` and no items** (F5's probe,
   *  2026-10-04 — not the 404 the query's doc suggests); a bodiless 404 is an unknown
   *  participant, survey, metric or scope. */
  history(
    participantId: string,
    address: IFigureAddress,
    page: number,
    signal?: AbortSignal
  ): Promise<IFigureHistory> {
    return apiRequest<unknown>(
      `${cellPath(participantId, address)}/events?page=${page}&pageSize=${HISTORY_PAGE_SIZE}`,
      { signal }
    ).then(historyOf);
  }
};
